// ─── Shared validation helpers ────────────────────────────────────────────────

const VALIDATION_MESSAGES = {
  required: 'This field is required.',
  email:    'Please enter a valid email address.',
  phone:    'Please enter a valid phone number.',
  zip:      'Please enter a valid ZIP / postal code.',
  date:     'Please enter a valid future date.',
  maxlen:   (n) => `Maximum ${n} characters allowed.`,
};

function _isValidEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
function _isValidPhone(v) { return /^\+?[\d\s\-()\[\]]{7,20}$/.test(v); }
function _isValidZip(v)   { return /^(?=.*\d)[a-zA-Z0-9\s\-]{3,10}$/.test(v); }
function _isValidDate(v) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(v);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d >= today;
}

function _isZipField(field) {
  return /zip|postal/i.test(`${field.name} ${field.id} ${field.placeholder}`);
}

function _isDateField(field) {
  return field.type === 'date' || /\\d\{4\}/.test(field.getAttribute('pattern') || '');
}

function getInputError(field) {
  const value = field.value.trim();
  const required = field.hasAttribute('required') || field.getAttribute('aria-required') === 'true';

  if (required && value === '') return field.dataset.errorRequired || VALIDATION_MESSAGES.required;
  if (value === '') return null;

  if (field.type === 'email' && !_isValidEmail(value)) return field.dataset.errorEmail || VALIDATION_MESSAGES.email;
  if (field.type === 'tel'   && !_isValidPhone(value)) return field.dataset.errorPhone || VALIDATION_MESSAGES.phone;
  if (_isZipField(field)     && !_isValidZip(value))   return field.dataset.errorZip   || VALIDATION_MESSAGES.zip;
  if (_isDateField(field)    && !_isValidDate(value))  return field.dataset.errorDate  || VALIDATION_MESSAGES.date;
  if (field.maxLength > 0 && value.length > field.maxLength) {
    return field.dataset.errorMaxlength || VALIDATION_MESSAGES.maxlen(field.maxLength);
  }
  return null;
}

function getSelectError(select) {
  if (!select.hasAttribute('required')) return null;
  const v = select.value;
  return (v === '' || v === '---') ? (select.dataset.errorRequired || VALIDATION_MESSAGES.required) : null;
}

// ─── Default error display ────────────────────────────────────────────────────
// Used by contact-form and newsletter-form.
// Inserts a <p> element after the field's control wrapper and tags it with
// data-error-for so it can be found and removed reliably.

function showDefaultError(field, message) {
  clearDefaultError(field);
  const errorId = 'error-for-' + CSS.escape(field.id || field.name);

  field.setAttribute('aria-invalid', 'true');
  field.setAttribute('aria-describedby', errorId);
  field.classList.add('field__input--error');

  const p = document.createElement('p');
  p.id = errorId;
  p.className = 'field__error-message';
  p.setAttribute('role', 'alert');
  p.setAttribute('data-error-for', field.id || field.name);
  p.innerHTML = `<svg class="icon" aria-hidden="true" focusable="false">
          <use href="#icon-error">
        </use></svg><span>${message}</span>`;

  const anchor = field.closest('.field__control') || field.closest('.field') || field.parentElement;
  anchor.insertAdjacentElement('afterend', p);
}

function clearDefaultError(field) {
  field.removeAttribute('aria-invalid');
  field.removeAttribute('aria-describedby');
  field.classList.remove('field__input--error');
  const key = CSS.escape(field.id || field.name);
  (field.closest('form') || document).querySelector(`[data-error-for="${key}"]`)?.remove();
}

// ─── Base form validator ──────────────────────────────────────────────────────

class FormValidator {
  constructor(form) {
    this.form = form;
    form.setAttribute('novalidate', '');
    form.addEventListener('submit', this._onSubmit.bind(this), true);
    this._attachListeners();
  }

  _getInputs()  { return this.form.querySelectorAll('.field__input'); }
  _getSelects() { return this.form.querySelectorAll('.select__select'); }

  _attachListeners() {
    this._getInputs().forEach((input) => {
      input.addEventListener('input', () => this._clearError(input));
      input.addEventListener('blur',  () => this._runInput(input));
    });
    this._getSelects().forEach((select) => {
      select.addEventListener('change', () => this._runSelect(select));
    });
  }

  _onSubmit(event) {
    let valid = true;
    this._getInputs().forEach((f)  => { if (!this._runInput(f))  valid = false; });
    this._getSelects().forEach((s) => { if (!this._runSelect(s)) valid = false; });
    if (!valid) { event.preventDefault(); event.stopImmediatePropagation(); }
  }

  _runInput(field) {
    const error = getInputError(field);
    if (error) { this._showError(field, error); return false; }
    this._clearError(field);
    return true;
  }

  _runSelect(select) {
    const error = getSelectError(select);
    if (error) { this._showError(select, error); return false; }
    this._clearError(select);
    return true;
  }

  // Subclasses can override for custom error display
  _showError(field, message) { showDefaultError(field, message); }
  _clearError(field)         { clearDefaultError(field); }
}

// ─── Contact form ─────────────────────────────────────────────────────────────

class ContactFormValidator extends FormValidator {}

// ─── Newsletter form ──────────────────────────────────────────────────────────

class NewsletterFormValidator extends FormValidator {}

// ─── Initialization ───────────────────────────────────────────────────────────

const FORM_CONFIGS = [
  { selector: '.contact-form',    Validator: ContactFormValidator },
  { selector: '.newsletter-form', Validator: NewsletterFormValidator },
];

function initForm(el, Validator) {
  if (el.dataset.validationInit) return;
  el.dataset.validationInit = 'true';
  new Validator(el);
}

function initAll(root = document) {
  FORM_CONFIGS.forEach(({ selector, Validator }) => {
    root.querySelectorAll(selector).forEach((el) => initForm(el, Validator));
    // Also check the root element itself (e.g. when called with an injected node)
    if (root instanceof Element && root.matches(selector)) initForm(root, Validator);
  });
}

initAll();

// Handle forms injected into DOM later (popups, modals cloning <template>, etc.)
new MutationObserver((mutations) => {
  for (const { addedNodes } of mutations) {
    for (const node of addedNodes) {
      if (node instanceof Element) initAll(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target || document.getElementById(`shopify-section-${event.detail?.sectionId}`);
  if (section) initAll(section);
});
